// src/features/tickets/components/CustomerTicketsPanel.tsx
//
// Bereich "Tickets" im Stammdatenfenster eines Kunden (EditCustomerModal).
//
// Oben: Zuordnungsregeln des Kunden (Absenderadresse bzw. Domain -> Kunde),
// mit Anlegen und Loeschen. Die Regeln steuern die automatische Zuordnung
// eingehender Mails (Datenbankfunktion match_ticket_customer).
//
// Darunter: alle Tickets des Kunden inklusive geschlossener, dazu die
// Gruppentickets seiner Firmengruppe (Gesellschaft offen). Suche, Gesamtzahl
// und "Weitere laden" wie in der Ticketliste; die Daten liefert
// ticketService.listTicketsPage mit Kundenfilter. Ein Klick oeffnet das Ticket
// in einem neuen Browser-Tab, damit das Stammdatenfenster offen bleibt.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Search, Trash2, Loader } from 'lucide-react';
import { useAuth } from '../../auth';
import { staffService } from '../../../services/staff/StaffService';
import { ticketService } from '../services/TicketService';
import { TicketList } from './TicketList';
import { TicketAbsenderRegel, TicketActor, TicketListView } from '../types';

interface CustomerTicketsPanelProps {
  customerId: string;
}

const PAGE_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 300;

const formatCount = (n: number): string => n.toLocaleString('de-DE');

export const CustomerTicketsPanel: React.FC<CustomerTicketsPanelProps> = ({ customerId }) => {
  const { user } = useAuth();

  // Handelnde Person fuer das Anlegen von Regeln (Audit-Felder der Regel)
  const [actor, setActor] = useState<TicketActor>({ staff_id: null, staff_email: user?.email ?? '' });

  useEffect(() => {
    let cancelled = false;
    const resolve = async () => {
      if (!user?.email) return;
      try {
        const allStaff = await staffService.getActiveStaff();
        const me = allStaff.find(s => s.email.toLowerCase() === user.email.toLowerCase());
        if (!cancelled) {
          setActor({
            staff_id: me?.id ?? null,
            staff_email: me?.email ?? user.email,
            staff_full_name: me?.fullName
          });
        }
      } catch (error) {
        console.error('[CustomerTicketsPanel] Staff-Resolution-Error:', error);
      }
    };
    resolve();
    return () => {
      cancelled = true;
    };
  }, [user?.email]);

  // ===========================================================================
  // Zuordnungsregeln
  // ===========================================================================

  const [regeln, setRegeln] = useState<TicketAbsenderRegel[]>([]);
  const [regelnLoading, setRegelnLoading] = useState(true);
  const [neueRegel, setNeueRegel] = useState('');
  const [regelSaving, setRegelSaving] = useState(false);
  const [regelError, setRegelError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const loadRegeln = useCallback(async () => {
    setRegelnLoading(true);
    const data = await ticketService.listSenderRules(customerId);
    setRegeln(data);
    setRegelnLoading(false);
  }, [customerId]);

  useEffect(() => {
    loadRegeln();
  }, [loadRegeln]);

  // Eingabe mit @ in der Mitte = Absenderadresse, sonst Domain. Ein fuehrendes
  // @ (z. B. "@firma.de") wird als Domain verstanden.
  const handleRegelAnlegen = async () => {
    const roh = neueRegel.trim().toLowerCase();
    if (!roh) return;
    const istDomainMitAt = roh.startsWith('@') && roh.indexOf('@', 1) === -1;
    const art: 'adresse' | 'domain' = !istDomainMitAt && roh.includes('@') ? 'adresse' : 'domain';
    const wert = istDomainMitAt ? roh.substring(1) : roh;

    setRegelSaving(true);
    setRegelError(null);
    const result = await ticketService.createSenderRule(art, wert, customerId, actor, 'stammdaten');
    setRegelSaving(false);
    if (!result.success) {
      setRegelError(result.error ?? 'Die Regel konnte nicht angelegt werden.');
      return;
    }
    setNeueRegel('');
    loadRegeln();
  };

  const handleRegelLoeschen = async (regel: TicketAbsenderRegel) => {
    const anzeige = regel.art === 'domain' ? `@${regel.wert}` : regel.wert;
    if (!window.confirm(`Regel für ${anzeige} löschen? Künftige Mails werden dann wieder automatisch zugeordnet.`)) return;
    setDeletingId(regel.id);
    const result = await ticketService.deleteSenderRule(regel.id);
    setDeletingId(null);
    if (!result.success) {
      alert(`Die Regel konnte nicht gelöscht werden: ${result.error ?? 'Unbekannter Fehler'}`);
      return;
    }
    loadRegeln();
  };

  // ===========================================================================
  // Tickets des Kunden
  // ===========================================================================

  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [tickets, setTickets] = useState<TicketListView[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const requestIdRef = useRef(0);

  useEffect(() => {
    const trimmed = searchTerm.trim();
    if (trimmed === '') {
      setDebouncedSearch('');
      return;
    }
    const timer = setTimeout(() => setDebouncedSearch(trimmed), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    const requestId = ++requestIdRef.current;
    const load = async () => {
      setLoading(true);
      const result = await ticketService.listTicketsPage({
        view: 'all',
        current_staff_id: null,
        include_closed: true,
        search: debouncedSearch || undefined,
        limit: PAGE_SIZE,
        offset: 0,
        customer_id: customerId
      });
      if (requestId !== requestIdRef.current) return;
      setTickets(result.success ? result.tickets : []);
      setTotalCount(result.success ? result.total_count : 0);
      setLoadError(!result.success);
      setLoading(false);
    };
    load();
  }, [customerId, debouncedSearch]);

  const handleLoadMore = async () => {
    if (loadingMore) return;
    const requestId = requestIdRef.current;
    setLoadingMore(true);
    const result = await ticketService.listTicketsPage({
      view: 'all',
      current_staff_id: null,
      include_closed: true,
      search: debouncedSearch || undefined,
      limit: PAGE_SIZE,
      offset: tickets.length,
      customer_id: customerId
    });
    if (requestId === requestIdRef.current) {
      if (result.success) {
        setTickets(prev => {
          const known = new Set(prev.map(t => t.id));
          return [...prev, ...result.tickets.filter(t => !known.has(t.id))];
        });
        setTotalCount(result.total_count);
        setLoadError(false);
      } else {
        setLoadError(true);
      }
    }
    setLoadingMore(false);
  };

  const handleTicketClick = (ticketId: string) => {
    window.open(`/tickets/${ticketId}`, '_blank', 'noopener,noreferrer');
  };

  const counterText = useMemo(() => {
    if (loading) return 'Lade Tickets...';
    const shown = tickets.length < totalCount ? `, ${formatCount(tickets.length)} angezeigt` : '';
    const base = debouncedSearch
      ? `${formatCount(totalCount)} Treffer`
      : `${formatCount(totalCount)} Ticket${totalCount !== 1 ? 's' : ''}`;
    return `${base}${shown}`;
  }, [loading, tickets.length, totalCount, debouncedSearch]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      {/* Zuordnungsregeln */}
      <div>
        <div style={{
          backgroundColor: '#EFF6FF',
          border: '1px solid #BFDBFE',
          borderRadius: '6px',
          padding: '12px',
          marginBottom: '16px',
          fontSize: '14px',
          color: '#1E40AF'
        }}>
          <strong>Zuordnungsregeln:</strong> Mails von diesen Absenderadressen bzw. Domains werden automatisch
          diesem Kunden zugeordnet. Eine Adressregel hat Vorrang vor allem anderen, eine Domainregel greift,
          wenn der Absender nicht eindeutig bei einem Kunden hinterlegt ist. Regeln entstehen auch bei der
          Zuordnung im Ticket („künftig diesem Kunden zuordnen“).
        </div>

        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '8px', flexWrap: 'wrap' }}>
          <input
            type="text"
            value={neueRegel}
            onChange={(e) => { setNeueRegel(e.target.value); if (regelError) setRegelError(null); }}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleRegelAnlegen(); } }}
            placeholder="name@firma.de oder firma.de"
            style={{
              flex: 1,
              minWidth: '240px',
              maxWidth: '400px',
              padding: '8px 12px',
              border: '1px solid #D1D5DB',
              borderRadius: '6px',
              fontSize: '14px',
              boxSizing: 'border-box'
            }}
          />
          <button
            type="button"
            onClick={handleRegelAnlegen}
            disabled={regelSaving || neueRegel.trim() === ''}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              padding: '8px 14px',
              backgroundColor: '#3B82F6',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: 500,
              cursor: regelSaving || neueRegel.trim() === '' ? 'not-allowed' : 'pointer',
              opacity: regelSaving || neueRegel.trim() === '' ? 0.6 : 1
            }}
          >
            {regelSaving && <Loader size={14} style={{ animation: 'spin 1s linear infinite' }} />}
            Regel anlegen
          </button>
        </div>
        {regelError && (
          <div style={{ fontSize: '13px', color: '#B91C1C', marginBottom: '8px' }}>{regelError}</div>
        )}

        {regelnLoading ? (
          <div style={{ fontSize: '13px', color: '#9CA3AF' }}>Lädt…</div>
        ) : regeln.length === 0 ? (
          <div style={{ fontSize: '13px', color: '#9CA3AF' }}>Für diesen Kunden sind keine Regeln hinterlegt.</div>
        ) : (
          <div style={{ border: '1px solid #E5E7EB', borderRadius: '8px', overflow: 'hidden' }}>
            {regeln.map((regel, index) => (
              <div
                key={regel.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '12px',
                  padding: '8px 12px',
                  borderBottom: index < regeln.length - 1 ? '1px solid #F3F4F6' : 'none',
                  fontSize: '14px'
                }}
              >
                <div style={{ minWidth: 0 }}>
                  <span style={{ fontWeight: 500, color: '#111827', wordBreak: 'break-all' }}>
                    {regel.art === 'domain' ? `@${regel.wert}` : regel.wert}
                  </span>
                  <span style={{ marginLeft: '8px', fontSize: '12px', color: '#6B7280' }}>
                    {regel.art === 'domain' ? 'ganze Domain' : 'Absenderadresse'}
                    {regel.angelegt_von_email ? `, angelegt von ${regel.angelegt_von_email}` : ''}
                    {`, ${new Date(regel.created_at).toLocaleDateString('de-DE')}`}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => handleRegelLoeschen(regel)}
                  disabled={deletingId === regel.id}
                  title="Regel löschen"
                  style={{
                    flexShrink: 0,
                    display: 'inline-flex',
                    padding: '6px',
                    backgroundColor: '#FEE2E2',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: deletingId === regel.id ? 'not-allowed' : 'pointer'
                  }}
                >
                  {deletingId === regel.id
                    ? <Loader size={14} color="#DC2626" style={{ animation: 'spin 1s linear infinite' }} />
                    : <Trash2 size={14} color="#DC2626" />}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Tickets des Kunden */}
      <div>
        <div style={{ fontSize: '15px', fontWeight: 600, color: '#111827', marginBottom: '4px' }}>
          Tickets
        </div>
        <div style={{ fontSize: '13px', color: '#6B7280', marginBottom: '12px' }}>
          Alle Tickets dieses Kunden inklusive geschlossener, dazu die Tickets seiner Firmengruppe, bei denen
          die Gesellschaft noch offen ist. Ein Klick öffnet das Ticket in einem neuen Tab.
        </div>

        <div style={{ position: 'relative', maxWidth: '400px', marginBottom: '12px' }}>
          <Search
            size={16}
            style={{
              position: 'absolute',
              left: '12px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#9CA3AF',
              pointerEvents: 'none'
            }}
          />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Suche in Betreff, Nachrichten, Ticketnummer..."
            style={{
              width: '100%',
              padding: '8px 12px 8px 36px',
              border: '1px solid #D1D5DB',
              borderRadius: '6px',
              fontSize: '14px',
              boxSizing: 'border-box'
            }}
          />
        </div>

        <div style={{ fontSize: '13px', color: '#6B7280', marginBottom: '12px' }}>{counterText}</div>

        {loadError && !loading && (
          <div style={{
            padding: '10px 14px',
            marginBottom: '12px',
            backgroundColor: '#FEF2F2',
            border: '1px solid #FECACA',
            borderRadius: '6px',
            color: '#B91C1C',
            fontSize: '14px'
          }}>
            Die Tickets konnten nicht geladen werden. Bitte das Fenster schließen und erneut öffnen.
          </div>
        )}

        {loading ? (
          <div style={{ padding: '24px', textAlign: 'center', color: '#6B7280', fontSize: '14px' }}>
            Lade Tickets...
          </div>
        ) : (
          <TicketList
            tickets={tickets}
            onTicketClick={handleTicketClick}
            currentStaffId={actor.staff_id}
          />
        )}

        {!loading && tickets.length < totalCount && (
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: '16px' }}>
            <button
              type="button"
              onClick={handleLoadMore}
              disabled={loadingMore}
              style={{
                padding: '8px 16px',
                backgroundColor: 'white',
                color: '#374151',
                border: '1px solid #D1D5DB',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: 500,
                cursor: loadingMore ? 'default' : 'pointer',
                opacity: loadingMore ? 0.6 : 1
              }}
            >
              {loadingMore
                ? 'Lade weitere Tickets...'
                : `Weitere ${formatCount(Math.min(PAGE_SIZE, totalCount - tickets.length))} laden`}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
